import {
  describeServerAddress,
  isConnectionFailure,
  isRefusedReply,
  isUnsentRequest,
} from "./connectionDiagnosis";

export type LoginErrorMessage = {
  headline: string;
  hint?: string;
};

type ErrorShape = {
  status?: unknown;
  originalStatus?: unknown;
  data?: { message?: unknown; error?: unknown };
  error?: unknown;
};

const SUPPORT_HINT = "If this continues, click Get log and send the file to technical support.";

const firstText = (...values: unknown[]): string =>
  values.find((value): value is string => typeof value === "string" && value !== "") ?? "";

const isServerFault = (status: unknown): boolean => typeof status === "number" && status >= 500;

// RTK puts developer text in `error` for FETCH_ERROR, PARSING_ERROR and CUSTOM_ERROR (e.g.
// "TypeError: ..."); it is only fit for a customer when the server answered with an HTTP status.
const isCustomerSafeErrorText = (status: unknown): boolean => typeof status === "number";

const httpSuffix = (status: unknown): string =>
  typeof status === "number" ? ` (HTTP ${status})` : "";

export const describeLoginError = (
  error: unknown,
  requestUrl: string,
  pageOrigin: string,
): LoginErrorMessage => {
  if (isConnectionFailure(error)) {
    return {
      headline: `Unable to reach the LiftNet server (${describeServerAddress(
        requestUrl,
        pageOrigin,
      )}).`,
      hint: `Check the network connection and that the server is running. ${SUPPORT_HINT}`,
    };
  }
  const shape = (error ?? {}) as ErrorShape;
  // The server answered, but the application could not decode the reply as a LiftNet message (for
  // example a schema this build does not know): the parser's own message means nothing to a
  // customer.
  if (shape.status === "PARSING_ERROR") {
    return {
      headline: `The server sent a reply the application could not read${httpSuffix(
        shape.originalStatus,
      )}.`,
      hint: SUPPORT_HINT,
    };
  }
  // The server answered, but runs an API schema that changed this reply's type in a way this build
  // cannot read safely: the two are different releases. The refusal text itself is for support.
  if (isRefusedReply(error)) {
    return {
      headline:
        "The server runs a different LiftNet version, and this application cannot read its reply safely.",
      hint: SUPPORT_HINT,
    };
  }
  // Nothing was sent: the browser could not build the request (usually a bad API address).
  if (isUnsentRequest(error)) {
    return {
      headline: "The login request could not be sent (the API address may be invalid).",
      hint: SUPPORT_HINT,
    };
  }
  const message = firstText(
    shape.data?.message,
    shape.data?.error,
    isCustomerSafeErrorText(shape.status) ? shape.error : undefined,
  );
  const headline = message
    ? `The server returned an error: ${message}`
    : "The server returned an error.";
  return isServerFault(shape.status) ? { headline, hint: SUPPORT_HINT } : { headline };
};
